import styled from 'styled-components'
import { useCallback, useRef, useState } from 'react'

import { useOnClickOutsideMultiple } from 'utils/onClickOutside'
import { Menu } from 'components/Menu/MenuWithPortal'
import { IconDots } from 'components/Icons/IconDots'
import { Typography } from 'components/Typography/Typography'
import { IconClose } from 'components/Icons/IconClose'
import Checkbox from 'components/Checkbox/Checkbox'
import { Button } from 'components/Button/Button'
import { WITHDRAW_DIALOG_ID } from '../WithdrawDialog'
import { useIsMobile } from 'utils/useIsMobile'

const MenuTrigger = styled.div`
  display: flex;
`

const MenuContainer = styled.div`
  padding: 16px;
  width: 250px;
`

const Title = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 16px;
  border-bottom: 1px solid ${p => p.theme.colors.text.primary};
`

const Body = styled.div`
  margin-top: 16px;
`

const SelectAll = styled.div`
  display: flex;
  justify-content: space-between;
`

const ProvidersContainer = styled.div`
  padding: 20px 16px;
  border-radius: 8px;
  border: 1px solid ${p => p.theme.colors.border};
  background: ${p => p.theme.colors.background.secondary};
  margin-top: 16px;
  max-height: 180px;
  overflow: auto;
`

const Actions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 16px;
  gap: 16px;
`

interface Props {
  handleUpdateProtocols: (protocols: Record<string, boolean>) => void
  protocols: Record<string, boolean>
}

export const LiquidityProvidersMenu = ({ handleUpdateProtocols, protocols }: Props) => {
  const [selectedProtocols, setSelectedProtocols] = useState({ ...protocols })

  const [isMenuOpen, setMenuOpen] = useState(false)

  const menuTriggerRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  const isMobile = useIsMobile()

  const handleProtocolToggle = useCallback(
    (protocol: string) => {
      setSelectedProtocols({
        ...selectedProtocols,
        [protocol]: !selectedProtocols[protocol],
      })
    },
    [selectedProtocols]
  )

  const handleSelectAllNone = useCallback(() => {
    const allTrue = Object.values(selectedProtocols).every(value => value === true)
    setSelectedProtocols(Object.fromEntries(Object.keys(selectedProtocols).map(key => [key, !allTrue])))
  }, [selectedProtocols])

  const onClose = useCallback(() => setMenuOpen(false), [])

  useOnClickOutsideMultiple([menuTriggerRef, menuRef], onClose)

  const toggleMenu = useCallback(() => {
    setMenuOpen(!isMenuOpen)
    setSelectedProtocols({ ...protocols })
  }, [isMenuOpen, protocols])

  const handleSaveClick = useCallback(() => {
    setMenuOpen(!isMenuOpen)
    handleUpdateProtocols(selectedProtocols)
  }, [handleUpdateProtocols, isMenuOpen, selectedProtocols])

  const saveEnabled = Object.values(selectedProtocols).some(value => value === true)

  return (
    <div style={{ position: 'relative' }}>
      <MenuTrigger onClick={toggleMenu} ref={menuTriggerRef}>
        <IconDots />
      </MenuTrigger>
      {isMenuOpen && (
        <Menu
          ref={menuRef}
          anchorElement={menuTriggerRef}
          style={isMobile ? { marginLeft: -50, marginTop: -40 } : {}}
          scrollContainerId={WITHDRAW_DIALOG_ID}
        >
          <MenuContainer>
            <Title>
              <Typography variant="description" fontWeight="medium">
                Liquidity providers
              </Typography>
              <div onClick={toggleMenu} style={{ display: 'flex', cursor: 'pointer' }}>
                <IconClose />
              </div>
            </Title>
            <Body>
              <SelectAll>
                <Typography variant="body" color="secondary">
                  Select all / none
                </Typography>
                <Checkbox
                  checked={Object.values(selectedProtocols).every(value => value === true)}
                  onChange={handleSelectAllNone}
                />
              </SelectAll>
              <ProvidersContainer>
                {Object.keys(selectedProtocols).map(p => (
                  <Typography
                    key={p}
                    variant="caption"
                    fontWeight="bold"
                    style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}
                  >
                    {p}
                    <div style={{ marginLeft: 'auto' }}>
                      <Checkbox checked={selectedProtocols[p]} onChange={() => handleProtocolToggle(p)} />
                    </div>
                  </Typography>
                ))}
              </ProvidersContainer>
            </Body>
            <Actions>
              <Button outlined style={{ width: '50%', height: 36, padding: 0 }} onClick={toggleMenu}>
                Cancel
              </Button>
              <Button
                style={{ width: '50%', height: 36, padding: 0 }}
                onClick={handleSaveClick}
                disabled={!saveEnabled}
              >
                Save
              </Button>
            </Actions>
          </MenuContainer>
        </Menu>
      )}
    </div>
  )
}
